import React from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Eye, RefreshCw, Trash2, FileText } from 'lucide-react';
import type { Theme } from '../../themes/theme';

import type { ColumnConfig } from '../../hooks/useColumnManager';
import type { TableItem } from '../../services/driveService';
import type { ApiDocument, PDFSnippet, SavedMessage } from '../../services/api';
import type { SavedContent } from '../../services/savedContentService';

export interface DriveTableRowProps {
  theme: Theme;
  item: TableItem;
  visibleColumns: ColumnConfig[];
  isSelected: boolean;
  handleDocumentToggle: (id: string) => void;
  handleItemOpen: (item: TableItem) => void;
  renderCellContent: (
    column: ColumnConfig,
    item: TableItem,
    processingSummary?: string
  ) => React.ReactNode;
  currentUser: any;
  processingSummary?: string;
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
  menuPosition: Record<string, { top?: number; bottom?: number; left: number }>;
  menuRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  menuButtonRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  onDocumentReprocess?: (documentId: string) => void;
  onDocumentDelete?: (doc: ApiDocument) => Promise<void> | void;
  onViewConceptualSummary?: (doc: ApiDocument) => void;
  onSnippetDelete?: (snippet: PDFSnippet) => Promise<void> | void;
  onSavedMessageDelete?: (message: SavedMessage) => Promise<void> | void;
  onStudioResultDelete?: (result: SavedContent) => Promise<void> | void;
}

const DriveTableRow: React.FC<DriveTableRowProps> = ({
  theme,
  item,
  visibleColumns,
  isSelected,
  handleDocumentToggle,
  handleItemOpen,
  renderCellContent,
  currentUser,
  processingSummary,
  openMenuId,
  setOpenMenuId,
  menuPosition,
  menuRefs,
  menuButtonRefs,
  onDocumentReprocess,
  onDocumentDelete,
  onViewConceptualSummary,
  onSnippetDelete,
  onSavedMessageDelete,
  onStudioResultDelete,
}) => (
  <div
    className={`group flex items-center gap-2 px-3 py-2 border-b ${
      theme.colors.border.primary
    } hover:${theme.colors.bg.secondary} cursor-pointer transition-all duration-150 min-w-max ${
      isSelected ? theme.colors.bg.tertiary : ''
    }`}
    onClick={() => handleItemOpen(item)}
  >
    {visibleColumns.map((column) => {
      if (column.id === 'checkbox') {
        return (
          <div key={column.id} className="flex-shrink-0" style={{ width: column.defaultWidth }}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                handleDocumentToggle(item.id);
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 flex-shrink-0 cursor-pointer"
            />
          </div>
        );
      }

      if (column.id === 'menu') {
        return (
          <div key={column.id} className="flex-shrink-0" style={{ width: column.defaultWidth }}>
            <button
              ref={(el) => {
                menuButtonRefs.current[item.id] = el;
              }}
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(openMenuId === item.id ? null : item.id);
              }}
              className="w-8 h-6 flex-shrink-0 flex items-center justify-center rounded hover:bg-nb-accent transition-all opacity-0 group-hover:opacity-100"
              title="Más opciones"
            >
              <MoreVertical className={`w-4 h-4 ${theme.colors.text.secondary}`} />
            </button>
          </div>
        );
      }

      return (
        <div key={column.id} className="flex-shrink-0" style={{ width: column.defaultWidth }}>
          {renderCellContent(column, item, processingSummary)}
        </div>
      );
    })}

    {openMenuId === item.id &&
      menuPosition[item.id] &&
      createPortal(
        <div
          ref={(el) => {
            menuRefs.current[item.id] = el;
          }}
          className={`fixed w-40 ${theme.colors.bg.input} border ${theme.colors.border.primary} rounded-lg shadow-xl z-[9999]`}
          onClick={(e) => e.stopPropagation()}
          style={{
            ...(menuPosition[item.id].top !== undefined
              ? { top: `${menuPosition[item.id].top}px` }
              : {}),
            ...(menuPosition[item.id].bottom !== undefined
              ? { bottom: `${menuPosition[item.id].bottom}px` }
              : {}),
            left: `${menuPosition[item.id].left}px`,
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenuId(null);
              handleItemOpen(item);
            }}
            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:${theme.colors.bg.secondary} transition-colors ${theme.colors.text.primary} border-b ${theme.colors.border.primary}`}
          >
            <Eye className={`w-4 h-4 ${theme.colors.text.primary}`} />
            {item.type === 'saved-message'
              ? 'Ver mensaje'
              : item.type === 'snippet'
                ? 'Ver snippet'
                : item.type === 'studio-result'
                  ? 'Ver resultado'
                  : 'Ver documento'}
          </button>

          {item.type === 'document' && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId(null);
                  onViewConceptualSummary?.(item.data as ApiDocument);
                }}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:${theme.colors.bg.secondary} transition-colors ${theme.colors.text.primary} border-b ${theme.colors.border.primary}`}
              >
                <FileText className={`w-4 h-4 ${theme.colors.text.primary}`} />
                Ver resumen conceptual
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId(null);
                  const doc = item.data as ApiDocument;
                  onDocumentReprocess?.(doc.id);
                }}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:${theme.colors.bg.secondary} transition-colors ${theme.colors.text.primary} border-b ${theme.colors.border.primary}`}
              >
                <RefreshCw className={`w-4 h-4 ${theme.colors.text.primary}`} />
                Reprocesar
              </button>

              {(item.origin === 'private' || currentUser?.is_admin) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(null);
                    onDocumentDelete?.(item.data as ApiDocument);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:${theme.colors.bg.secondary} transition-colors ${theme.colors.text.primary}`}
                >
                  <Trash2 className={`w-4 h-4 ${theme.colors.text.primary}`} />
                  Eliminar
                </button>
              )}
            </>
          )}

          {item.type === 'snippet' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(null);
                onSnippetDelete?.(item.data as PDFSnippet);
              }}
              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:${theme.colors.bg.secondary} transition-colors ${theme.colors.text.primary}`}
            >
              <Trash2 className={`w-4 h-4 ${theme.colors.text.primary}`} />
              Eliminar snippet
            </button>
          )}

          {item.type === 'saved-message' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(null);
                onSavedMessageDelete?.(item.data as SavedMessage);
              }}
              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:${theme.colors.bg.secondary} transition-colors ${theme.colors.text.primary}`}
            >
              <Trash2 className={`w-4 h-4 ${theme.colors.text.primary}`} />
              Eliminar mensaje
            </button>
          )}

          {item.type === 'studio-result' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(null);
                onStudioResultDelete?.(item.data as SavedContent);
              }}
              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:${theme.colors.bg.secondary} transition-colors ${theme.colors.text.primary}`}
            >
              <Trash2 className={`w-4 h-4 ${theme.colors.text.primary}`} />
              Eliminar resultado
            </button>
          )}
        </div>,
        document.body
      )}
  </div>
);

export default DriveTableRow;
